import { LitElement, TemplateResult, html } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { AdoptedStyleSheets } from '../../controllers/adopted-stylesheets';
import innerCSS from './se-search-panel.inner.pcss?inline';
import { MobileControllerLegacy } from '../../controllers/mobile-controller-legacy';
import { SeMainHeader } from '../se-main-header/se-main-header';
import { classMap } from 'lit/directives/class-map.js';
import {
  SeBackgroundAlignment,
  SeBackgroundCurveWidth,
} from '../se-background/se-background';
export const searchBackgrounds = ['primary-1', 'white'] as const;

export type SearchBackground = typeof searchBackgrounds[number];

export const searchVariants = ['nav', 'hero', 'default'] as const;

export type SearchVariant = typeof searchVariants[number];

/**
 * The SeSearchPanel component
 * @element se-search-panel
 */
@customElement('se-search-panel')
export class SeSearchPanel extends LitElement {
  private stylesheetController = new AdoptedStyleSheets(this, {
    encapsulatedCSS: innerCSS,
  });
  mobileControllerLegacy = new MobileControllerLegacy(this);

  @property({ type: String })
  variant: SearchVariant = 'default';

  /**
   * Sets background color vartiation.
   */
  @property({ type: String, attribute: 'background-color' })
  backgroundColor: SearchBackground;

  /**
   * Sets background image width. Only used in the search-hero.
   */
  @property({ type: String, attribute: 'background-width' })
  backgroundWidth: SeBackgroundCurveWidth = '50';

  /**
   * Sets background image position. Only used in the search-hero.
   */
  @property({ type: String, attribute: 'background-position' })
  backgroundPosition: SeBackgroundAlignment = 'right';

  /**
   * If at mobile screen.
   */
  @property({ type: Boolean })
  isMobile: boolean;

  render(): TemplateResult {
    const contentClasses = {
      ['se-search--content']: true,
      'is-nav': this.variant === 'nav',
      'is-hero': this.variant === 'hero',
    };
    return html`
      <section class="se-search--wrapper">
        <se-background
          variant="${this.variant === 'nav' && this.isMobile
            ? 'none'
            : 'curves'}"
          alignment="${this.backgroundPosition}"
          curve-width="${this.backgroundWidth}"
          color="${this.backgroundColor}"
          full-bleed
        >
          <se-container>
            <div class="se-search">
              <div class="${classMap(contentClasses)}">
                ${this.variant === 'nav' ? this.buttonIcon() : null}
                <slot name="top-content"></slot>
                <slot name="title"></slot>
                <slot name="search"></slot>
                <slot name="bottom-content"></slot>
              </div>
            </div>
          </se-container>
        </se-background>
      </section>
    `;
  }

  firstUpdated() {
    this.isMobile = this.mobileControllerLegacy.isMobile;
  }

  updated() {
    this.isMobile = this.mobileControllerLegacy.isMobile;
  }

  buttonIcon() {
    if (this.isMobile) {
      return html`
        <se-button
          @click=${this.toggleSearchPanel}
          variant="none"
          class="top-right"
          id="search-panel-button"
          aria-label="close search"
        >
          <outline-icon
            slot="right"
            color="primary-1"
            size="18px"
            library="system"
            name="x"
          ></outline-icon>
        </se-button>
      `;
    } else {
      return html`
        <se-button
          @click=${this.toggleSearchPanel}
          variant="none"
          class="top-right"
          id="search-panel-button"
          aria-label="close search"
        >
          <outline-icon
            slot="right"
            color="primary-1"
            size="32px"
            library="system"
            name="x-circle-fill"
          ></outline-icon>
        </se-button>
      `;
    }
  }

  /**
   * For is-nav variant. Main header event handler for open/close the search panel.
   */
  toggleSearchPanel() {
    const header = document.querySelector('se-main-header') as SeMainHeader;
    header.toggleSearch();
  }
}
